// Heuristics for figuring out what kind of item the user just pasted or dropped.
// Files (image / PDF / video / font / generic) win first; failing that we look at text
// payloads and decide between URL and freeform text.

import type { MoodboardItem } from '@backend/lib/db/schema/moodboard'

// Magic prefix used to tunnel a canvas selection through the system clipboard. Lives
// inside text/plain so OSes don't strip it. We pick a sentinel string unlikely to
// appear in real user clipboards. Versioned so we can change the payload shape later.
const INTERNAL_CLIP_PREFIX = 'MOODBOARD_CLIP_v1'

export function encodeInternalClip(items: MoodboardItem[]): string {
  return `${INTERNAL_CLIP_PREFIX}${JSON.stringify({ items })}`
}

export function decodeInternalClip(text: string): MoodboardItem[] | null {
  if (!text.startsWith(INTERNAL_CLIP_PREFIX)) return null
  try {
    const payload = JSON.parse(text.slice(INTERNAL_CLIP_PREFIX.length)) as unknown
    if (
      typeof payload === 'object' &&
      payload !== null &&
      Array.isArray((payload as { items?: unknown }).items)
    ) {
      return (payload as { items: MoodboardItem[] }).items
    }
  } catch {
    /* not our magic — fall through */
  }
  return null
}

export type PasteIntent =
  | { kind: 'files'; files: File[] }
  | { kind: 'url'; url: string }
  | { kind: 'text'; text: string }
  | { kind: 'internal'; items: MoodboardItem[] }
  | { kind: 'empty' }

// Browsers expose `text/uri-list` for proper URL drops; some sources put the URL into
// `text/plain` instead. Either way, we treat anything that parses as an absolute http(s)
// URL on the first line as a URL drop.
function looksLikeUrl(s: string): string | null {
  const first = s.split(/\r?\n/)[0]?.trim()
  if (!first) return null
  try {
    const u = new URL(first)
    if (u.protocol === 'http:' || u.protocol === 'https:') return u.toString()
  } catch {
    /* not a URL */
  }
  return null
}

export function classifyClipboardData(data: DataTransfer): PasteIntent {
  // Magic internal payload wins first — our canvas-selection copy tunnels through
  // text/plain. Any other paste source overwrites it via the system clipboard so
  // there's no stale-state hijack.
  const plain = data.getData('text/plain')
  if (plain) {
    const internal = decodeInternalClip(plain)
    if (internal) return { kind: 'internal', items: internal }
  }

  // Files next — pasted/dropped binary content. Skip empties (some clipboard payloads
  // include a synthetic File entry with size 0 for HTML fragments).
  const files = data.files ? Array.from(data.files).filter((f) => f.size > 0) : []
  if (files.length > 0) return { kind: 'files', files }

  const uriList = data.getData('text/uri-list')
  const uriFromList = uriList ? looksLikeUrl(uriList) : null
  if (uriFromList) return { kind: 'url', url: uriFromList }

  if (!plain) return { kind: 'empty' }

  const uriFromPlain = looksLikeUrl(plain)
  if (uriFromPlain) return { kind: 'url', url: uriFromPlain }

  return { kind: 'text', text: plain }
}

// Distance offsets used when multiple files are dropped together so they don't stack
// exactly on top of each other.
export function staggerPosition(base: { x: number; y: number }, index: number) {
  const STAGGER = 24
  return { x: base.x + index * STAGGER, y: base.y + index * STAGGER }
}
